import { Delete } from '@mui/icons-material';
import { Divider } from '@mui/material';
import { DateTime } from 'luxon';
import { Control, Controller, useWatch } from 'react-hook-form';

import { Button } from '@/components/Button';
import { DurationInput } from '@/components/DurationInput';
import { Input } from '@/components/Input';
import { InputWrapper } from '@/components/InputWrapper/InputWrapper';

import { TimeButtonsSection } from '../TimeButtonsSection';
import { FormValues } from '../../types';

import s from './DateEntryItem.module.scss';

type DateEntryItemProps = {
  control: Control<FormValues>;
  index: number;
  entry: { startDate?: string; duration?: string } | undefined;
  errors: any;
  showDeleteButton: boolean;
  onDelete: () => void;
  setValue: (name: any, value: any) => void;
  autoFocusDuration?: boolean;
  isDateLocked?: boolean;
};

const DateEntryItem: React.FC<DateEntryItemProps> = ({
  control,
  index,
  entry,
  errors,
  showDeleteButton,
  onDelete,
  setValue,
  autoFocusDuration,
  isDateLocked,
}) => {
  const currentDuration = useWatch({ control, name: `dateEntries.${index}.duration` as any });

  const formattedLockedDate = entry?.startDate
    ? DateTime.fromISO(entry.startDate).setLocale('ru').toLocaleString(DateTime.DATE_FULL)
    : '';

  return (
    <div className={s.date_entry}>
      {index > 0 && <Divider sx={{ mb: 1, borderWidth: '1px' }} />}
      <div className={s.date_entry__content}>
        <div className={s.date_entry__header}>
          <InputWrapper label="Дата">
            {isDateLocked ? (
              <div className={s.date_entry__locked_date}>{formattedLockedDate}</div>
            ) : (
              <Controller
                control={control}
                name={`dateEntries.${index}.startDate`}
                render={({ field: dateField }) => (
                  <Input
                    {...dateField}
                    type="date"
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        lang: 'ru',
                      },
                    }}
                    error={
                      !!errors.dateEntries?.[index]?.startDate ||
                      (!entry?.startDate && !!entry?.duration)
                    }
                    helperText={errors.dateEntries?.[index]?.startDate?.message || ''}
                  />
                )}
              />
            )}
          </InputWrapper>
          <div className={s.date_entry__time_input}>
            <InputWrapper label="Время">
              <Controller
                control={control}
                name={`dateEntries.${index}.startTime`}
                render={({ field }) => <Input {...field} value={field.value ?? ''} type="time" />}
              />
            </InputWrapper>
          </div>
          {showDeleteButton && !isDateLocked && (
            <Button
              variant="outlined"
              onClick={onDelete}
              sx={{ minWidth: 'auto', padding: '8px', borderRadius: '8px' }}
            >
              <Delete fontSize="small" />
            </Button>
          )}
        </div>

        <InputWrapper
          label="Затрачено времени"
          hint={`Формат: 1ч 30м, 1:30, 90, 1 30, 1д 2ч 30м, 1н 3д 2ч, 2w 1d 5h.
Можно использовать как калькулятор: 1ч 20м + 40м, 8ч - 1ч 30м, 45м * 3`}
        >
          <div className={s.time}>
            <DurationInput
              control={control}
              name={`dateEntries.${index}.duration`}
              error={errors.dateEntries?.[index]?.duration}
              autoFocus={autoFocusDuration}
              required
            />
          </div>
        </InputWrapper>

        <TimeButtonsSection
          currentValue={currentDuration || ''}
          onChange={(value) => setValue(`dateEntries.${index}.duration`, value)}
          className={s.time_buttons}
        />
      </div>
    </div>
  );
};

export default DateEntryItem;
